import React from 'react';
import I18n from 'i18n-js';
import { RCDrawer, RCIcon, RCLoadingBars, RCPopover, RCText, useMediaQuery } from '@reverbdotcom/cadence/components';
import { BellIcon } from '@reverbdotcom/cadence/icons/react';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { useNotificationsBellCount } from './notifications_bell_count_hook';
import { useNotificationsCenter } from './notifications_center_hook';
import ActionsSection from './actions_section';
import ActionsRollupView from './actions_rollup_view';
import TasksSection from './tasks_section';
import StreamSection from './stream_section';

export const COMPONENT_NAME = 'NotificationsCenterPopover';

export const MAX_BELL_COUNT = 99;

// Matches HeaderDropdown's MOUSEOUT_BUFFER_MS, so the bell dismisses on the same
// beat as the menu and cart beside it.
export const HOVER_CLOSE_BUFFER_MS = 600;

type PanelView = 'root' | 'actionsRollup';

export default function NotificationsCenterPopover() {
  const { count } = useNotificationsBellCount();
  const [isOpen, setIsOpen] = React.useState(false);
  const [view, setView] = React.useState<PanelView>('root');
  const { data, loading } = useNotificationsCenter(isOpen);
  // Null until the browser reports, and on the server, so fall back to desktop.
  const isMobile = useMediaQuery('mobile') ?? false;

  function handleOpenChange(open: boolean) {
    // RCPopover's own click handling toggles, so a press on the bell while hover
    // already opened the panel would immediately shut it. HeaderDropdown has the
    // same shape and resolves it the same way: the trigger only ever opens.
    // Scoped to presses on the bell itself, so escape and outside presses still
    // close normally. Only desktop hovers, so only desktop needs the guard - on
    // mobile the same press is the drawer's own close.
    if (!isMobile && !open && anchorPressRef.current) {
      anchorPressRef.current = false;
      return;
    }

    if (open) {
      trackEvent({
        eventName: MParticleEventName.OpenedNotificationPanel,
        componentName: COMPONENT_NAME,
      });
    }

    setIsOpen(open);
    if (!open) { setView('root'); }
  }

  const actions = data?.notificationActions?.actions ?? [];
  const rollup = data?.notificationActions?.rollup ?? null;
  const tasks = data?.notificationTasks?.tasks ?? [];

  const showingLoader = loading && !data;

  const closeTimerRef = React.useRef<number | undefined>(undefined);
  const anchorPressRef = React.useRef(false);

  function cancelPendingClose() {
    window.clearTimeout(closeTimerRef.current);
  }

  function scheduleClose() {
    cancelPendingClose();
    closeTimerRef.current = window.setTimeout(
      () => handleOpenChange(false),
      HOVER_CLOSE_BUFFER_MS,
    );
  }

  function handleAnchorEnter() {
    cancelPendingClose();
    if (!isOpen) { handleOpenChange(true); }
  }

  React.useEffect(() => cancelPendingClose, []);

  const showingRollup = view === 'actionsRollup';
  const bellCount = count > MAX_BELL_COUNT
    ? I18n.t('discovery.notificationsCenter.maxCount', { count: MAX_BELL_COUNT })
    : count || null;

  const bell = (
    <button
      type="button"
      className="reverb-header__nav__link reverb-header__nav__link--notification-icon"
      onMouseEnter={isMobile ? undefined : handleAnchorEnter}
      onMouseLeave={isMobile ? undefined : scheduleClose}
      onPointerDown={() => { anchorPressRef.current = true; }}
    >
      <div
        className="reverb-header__nav__link__icon notifications-center__bell"
        data-notification-count={bellCount}
      >
        <RCIcon icon={<BellIcon />} />
      </div>
      <div className="reverb-header__nav__link__label">
        {I18n.t('discovery.reverbHeader.notifications')}
      </div>
      <span className="reverb-header__count-badge">
        {count > 0 && I18n.t('discovery.reverbHeader.notificationCount', { count })}
      </span>
    </button>
  );

  const panel = (
    <div
      className="notifications-center__viewport"
      data-testid="notifications-center-placeholder"
      onMouseEnter={isMobile ? undefined : cancelPendingClose}
      onMouseLeave={isMobile ? undefined : scheduleClose}
    >
      {showingLoader ? (
        <div className="d-flex fx-justify-center ptb-8">
          <RCLoadingBars />
        </div>
      ) : (
        <div
          className="notifications-center__slider"
          data-showing-rollup={showingRollup || undefined}
        >
          <div
            className="notifications-center__pane notifications-center__pane--root"
            aria-hidden={showingRollup}
          >
            <div className="notifications-center">
              {/* The drawer renders its own titled header, so only the popover
                  needs one of its own. */}
              {!isMobile && (
                <header className="notifications-center__header">
                  <RCText.Title size="600" htmlTag="h2">
                    {I18n.t('discovery.notificationsCenter.title')}
                  </RCText.Title>
                </header>
              )}
              <ActionsSection
                rollup={rollup}
                onOpenRollup={() => setView('actionsRollup')}
              />
              <TasksSection tasks={tasks} />
              <StreamSection />
            </div>
          </div>

          <div
            className="notifications-center__pane notifications-center__pane--rollup"
            aria-hidden={!showingRollup}
          >
            <ActionsRollupView
              actions={actions}
              onBack={() => setView('root')}
            />
          </div>
        </div>
      )}
    </div>
  );

  // Mobile web gets a full-viewport drawer, matching the menu beside it. The
  // popover's hover-to-open has no meaning on touch, so the drawer opens on
  // press and closes from its own header button.
  if (isMobile) {
    return (
      <RCDrawer
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        title={I18n.t('discovery.notificationsCenter.title')}
        position="right"
        trigger={bell}
      >
        {panel}
      </RCDrawer>
    );
  }

  return (
    <RCPopover
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      position="bottom-end"
      size="large"
      anchor={bell}
    >
      {panel}
    </RCPopover>
  );
}
